/**
 * Meeting Analytics Page
 * Comprehensive analytics and insights for meeting data
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, Clock, Users, CheckSquare, TrendingUp, Calendar,
  Video, Building2, PieChart, Activity, Loader2, RefreshCw,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingAnalytics = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('30');
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/api/meeting-intelligence/analytics?token=${token}&days=${period}`);
      setAnalytics(res.data.analytics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [token, period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const data = analytics || {
    summary: { total_meetings: 0, completed_meetings: 0, avg_duration_minutes: 0, total_meeting_hours: 0, tasks_created: 0 },
    by_status: {},
    by_type: {},
    by_weekday: [],
    by_hour: [],
    top_attendees: [],
    tasks_by_status: {},
    weekly_trends: [],
    top_topics: []
  };

  // Calculate percentages for meeting types
  const totalMeetings = data.summary.total_meetings || 1;
  const typePercentages = Object.entries(data.by_type || {}).map(([type, count]) => ({
    type,
    count,
    percentage: Math.round((count / totalMeetings) * 100)
  }));

  // Get max hour count for bar scaling
  const maxHourCount = Math.max(...(data.by_hour || []).map(h => h.count), 1);

  return (
    <div className="p-6 space-y-6" data-testid="meeting-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-blue-600" />
            Meeting Analytics
          </h1>
          <p className="text-slate-500">Insights and trends from your meetings</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Meetings</p>
                <p className="text-3xl font-bold">{data.summary.total_meetings}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-3xl font-bold">{data.summary.completed_meetings}</p>
              </div>
              <CheckSquare className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Duration</p>
                <p className="text-3xl font-bold">{data.summary.avg_duration_minutes}<span className="text-lg">m</span></p>
              </div>
              <Clock className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Hours</p>
                <p className="text-3xl font-bold">{data.summary.total_meeting_hours}<span className="text-lg">h</span></p>
              </div>
              <Activity className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Tasks Created</p>
                <p className="text-3xl font-bold">{data.summary.tasks_created}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-cyan-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Meeting Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-600" />
              Meeting Types
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {typePercentages.length > 0 ? (
              typePercentages.map(({ type, count, percentage }) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {type === 'teams' ? (
                        <Video className="h-4 w-4 text-purple-600" />
                      ) : type === 'hybrid' ? (
                        <Users className="h-4 w-4 text-amber-600" />
                      ) : (
                        <Building2 className="h-4 w-4 text-blue-600" />
                      )}
                      <span className="capitalize">{type}</span>
                    </div>
                    <span className="text-sm text-slate-500">{count} ({percentage}%)</span>
                  </div>
                  <Progress 
                    value={percentage} 
                    className={`h-2 ${type === 'teams' ? '[&>div]:bg-purple-500' : type === 'hybrid' ? '[&>div]:bg-amber-500' : '[&>div]:bg-blue-500'}`}
                  />
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-4">No meeting data available</p>
            )}
          </CardContent>
        </Card>

        {/* Most Active Attendees */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Most Active Attendees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_attendees?.length > 0 ? (
              <div className="space-y-3">
                {data.top_attendees.slice(0, 5).map((attendee, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                        {attendee.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{attendee.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{attendee.email}</p>
                      </div>
                    </div>
                    <Badge variant="secondary">{attendee.meeting_count} meetings</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No attendee data available</p>
            )}
          </CardContent>
        </Card>

        {/* Meetings by Day of Week */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Meetings by Day
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_weekday?.length > 0 ? (
              <div className="flex items-end justify-between h-40 gap-2">
                {data.by_weekday.map((day, idx) => {
                  const maxCount = Math.max(...data.by_weekday.map(d => d.count), 1);
                  const height = (day.count / maxCount) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className="w-full bg-blue-500 rounded-t transition-all"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-slate-500">{day.day}</span>
                      <span className="text-xs font-medium">{day.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No schedule data available</p>
            )}
          </CardContent>
        </Card>

        {/* Meetings by Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Peak Meeting Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_hour?.length > 0 ? (
              <div className="space-y-2">
                {data.by_hour
                  .filter(h => h.count > 0)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6)
                  .map((hour, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-16 text-sm text-slate-500">
                        {hour.hour.toString().padStart(2, '0')}:00
                      </span>
                      <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded"
                          style={{ width: `${(hour.count / maxHourCount) * 100}%` }}
                        />
                      </div>
                      <span className="w-8 text-sm font-medium text-right">{hour.count}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No hourly data available</p>
            )}
          </CardContent>
        </Card>

        {/* Top Discussion Topics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Top Discussion Topics
            </CardTitle>
            <CardDescription>Most frequently mentioned key points</CardDescription>
          </CardHeader>
          <CardContent>
            {data.top_topics?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.top_topics.slice(0, 15).map((topic, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline"
                    className={`${idx < 3 ? 'bg-blue-50 border-blue-200' : ''}`}
                  >
                    {topic.topic} ({topic.count})
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No topics extracted yet</p>
            )}
          </CardContent>
        </Card>

        {/* Task Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              Tasks from Meetings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.tasks_by_status || {}).length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(data.tasks_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="capitalize text-sm">{status.replace('_', ' ')}</span>
                    <Badge variant={status === 'completed' ? 'default' : status === 'overdue' ? 'destructive' : 'secondary'}>
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No task data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Weekly Meeting Trends
          </CardTitle>
          <CardDescription>Number of meetings per week</CardDescription>
        </CardHeader>
        <CardContent>
          {data.weekly_trends?.length > 0 ? (
            <div className="flex items-end justify-between h-32 gap-1">
              {data.weekly_trends.map((week, idx) => {
                const maxCount = Math.max(...data.weekly_trends.map(w => w.count), 1);
                const height = (week.count / maxCount) * 100;
                const prevCount = idx > 0 ? data.weekly_trends[idx - 1].count : week.count;
                const trend = week.count > prevCount ? 'up' : week.count < prevCount ? 'down' : 'same';
                
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-xs">
                      {trend === 'up' && <ArrowUp className="h-3 w-3 text-green-500" />}
                      {trend === 'down' && <ArrowDown className="h-3 w-3 text-red-500" />}
                      {trend === 'same' && <Minus className="h-3 w-3 text-slate-400" />}
                    </div>
                    <div 
                      className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all hover:from-blue-600 hover:to-blue-500"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                    <span className="text-xs text-slate-500">W{week.week}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">Not enough data for trends</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MeetingAnalytics;
